import { HelpCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { Button, TooltipTrigger } from "react-aria-components";

import { Switch } from "@/components/switch";
import { Tooltip } from "@/components/tooltip";
import { useUpdateBrand } from "@/features/brands/shared/hooks/useUpdateBrand";

interface BrandAiAssetsToggleProps {
  readonly brandId: number;
  readonly restrictAiAssets: boolean;
}

export function BrandAiAssetsToggle({
  brandId,
  restrictAiAssets,
}: BrandAiAssetsToggleProps) {
  const { mutate } = useUpdateBrand({ brandId });
  const [isSelected, setIsSelected] = useState(restrictAiAssets);

  // Keep local state in sync when the brand data refetches.
  useEffect(() => {
    setIsSelected(restrictAiAssets);
  }, [restrictAiAssets]);

  const handleChange = (value: boolean) => {
    setIsSelected(value);
    mutate(
      { restrictAiAssets: value },
      {
        // Revert the optimistic toggle if the save fails.
        onError: () => setIsSelected(!value),
      },
    );
  };

  return (
    <div className="flex items-center gap-sm">
      <TooltipTrigger delay={0}>
        <Button
          aria-label="More information about AI assets"
          className="cursor-default outline-hidden"
        >
          <HelpCircle className="size-4 text-fg-quinary" />
        </Button>
        <Tooltip>
          By turning this on, you do not allow design teams to use AI-generated
          assets in any designs for this brand. This setting can be overridden
          on individual requests.
        </Tooltip>
      </TooltipTrigger>
      <Switch
        isSelected={isSelected}
        labelPosition="left"
        onChange={handleChange}
      >
        Do not use AI Assets
      </Switch>
    </div>
  );
}
